/**
 * ACP-2092 (Section 508): composing the announcement for newly displayed validation
 * errors, so a screen reader reports them as they appear instead of leaving the user
 * to navigate back to a field to discover one.
 */

/** Text an element's `aria-labelledby` resolves to, if it has one. */
function namedBy(element: HTMLElement): string {
  const labelledBy = element.getAttribute('aria-labelledby');
  if (!labelledBy) return '';

  return labelledBy
    .split(/\s+/)
    .map((id) => document.getElementById(id)?.textContent?.trim())
    .filter(Boolean)
    .join(' ');
}

/** The name a screen reader would read for a control, however it is labelled. */
function accessibleName(element: HTMLElement): string {
  // A control whose accessible name is an instruction rather than a name — the upload
  // area reads "Upload file area for Upload Resume. Click or press Enter to choose a
  // file, …" — says which field it is through data-error-name instead.
  const errorName = element.dataset.errorName?.trim();
  if (errorName) return errorName;

  // A radio stands in for its whole group, so name the question rather than the option
  // that happened to come first — "Yes required" says nothing about what is missing.
  if (element instanceof HTMLInputElement && element.type === 'radio') {
    const group = element.closest('fieldset');
    const groupName = group && namedBy(group);
    if (groupName) return groupName;
  }

  const ariaLabel = element.getAttribute('aria-label')?.trim();
  if (ariaLabel) return ariaLabel;

  const labelledBy = namedBy(element);
  if (labelledBy) return labelledBy;

  if (element.id) {
    const label = document.querySelector<HTMLElement>(`label[for="${element.id}"]`)?.textContent?.trim();
    if (label) return label;
  }

  return element.closest('label')?.textContent?.trim() ?? '';
}

/**
 * Why a filled-in control is invalid, or '' when the name alone says enough.
 *
 * An empty control's name already carries "required", which is the whole story. A
 * control that holds a value failed on something else — a date range, a format, a
 * mismatch — and naming it alone read as "Date Served To required" for a field that was
 * filled in. Its message is what it is described by — the error, plus any hint the
 * design system appends, which is dropped.
 */
function reasonFor(element: HTMLElement): string {
  const isTextEntry =
    (element instanceof HTMLInputElement && !['checkbox', 'radio', 'file'].includes(element.type)) ||
    element instanceof HTMLTextAreaElement;
  if (!isTextEntry || !element.value.trim()) return '';

  // The design system appends its hint ("XXX-XX-XXXX") to the same aria-describedby, so
  // keep only the error descriptions when there are any.
  const ids = (element.getAttribute('aria-describedby') ?? '').split(/\s+/).filter(Boolean);
  const errorIds = ids.filter((id) => /error/i.test(id));

  return (errorIds.length > 0 ? errorIds : ids)
    .map((id) => document.getElementById(id)?.textContent?.trim())
    .filter(Boolean)
    .join(' ')
    .replace(/[.\s]+$/, '');
}

/** How a field is listed: its name, plus the reason when the name does not explain it. */
function describeField(element: HTMLElement): string {
  // A trailing period in a label ("…consent to the above.") would otherwise meet the
  // sentence's own and be read as "above..".
  const name = accessibleName(element).replace(/[.\s]+$/, '');
  if (!name) return '';

  const reason = reasonFor(element);

  return reason ? `${name} (${reason})` : name;
}

/**
 * One announcement naming every field that needs attention.
 *
 * Deliberately a single summary rather than `role="alert"` on each field: several
 * fields failing at once would queue several announcements that talk over each other,
 * which the ticket rules out ("multiple validation errors are communicated in a usable
 * manner"). Naming the fields is what makes it actionable — a bare "3 errors" leaves
 * the user hunting, and the per-field messages are generic ("This field is required"),
 * so they only identify anything once paired with the control's own name.
 *
 * Names are read from the controls rather than from the error map because the labels
 * live in the sub-step components, and a control's accessible name is exactly what a
 * screen reader would read out anyway.
 */
export function buildErrorAnnouncement(invalidControls: HTMLElement[]): string {
  if (invalidControls.length === 0) return '';

  const names = invalidControls.map(describeField).filter(Boolean);
  const count = invalidControls.length;
  const noun = count === 1 ? 'field needs' : 'fields need';
  const opening = `${count} ${noun} your attention`;

  if (names.length === 0) {
    return `${opening} on this page. Review the highlighted fields.`;
  }

  // The count and the list have to agree. A control with no discoverable name still
  // counts, so saying "3 fields need your attention: A, B." would send the user
  // looking for a third name that was never spoken.
  const unnamed = count - names.length;
  if (unnamed > 0) {
    const remainder = unnamed === 1 ? '1 other field' : `${unnamed} other fields`;

    return `${opening}: ${names.join(', ')}, and ${remainder}. Review the highlighted fields.`;
  }

  return `${opening}: ${names.join(', ')}.`;
}
